import { useEffect, useState } from 'react';
import { Calendar, FileText, MapPin, MessageCircle, Package, Phone, Scale, Truck, X } from 'lucide-react';
import { shipmentServiceReal } from '../services/shipmentServiceReal';

type PendingAssignmentDetailSheetProps = {
  shipmentId: string | null;
  open: boolean;
  onClose: () => void;
  title?: string;
};

const formatDateTime = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('tr-TR');
};

const haversineKm = (lat1?: number | null, lon1?: number | null, lat2?: number | null, lon2?: number | null) => {
  if ([lat1, lon1, lat2, lon2].some((v) => v == null)) return null;
  const toRad = (value: number) => (value * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad((lat2 as number) - (lat1 as number));
  const dLon = toRad((lon2 as number) - (lon1 as number));
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(lat1 as number)) * Math.cos(toRad(lat2 as number)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return Number((R * c).toFixed(1));
};

const estimateArrivalDuration = (distanceKm?: number | null) => {
  if (distanceKm == null) return '-';
  const averageSpeedKmh = distanceKm <= 15 ? 35 : distanceKm <= 80 ? 55 : 70;
  const totalMinutes = Math.max(1, Math.round((distanceKm / averageSpeedKmh) * 60));
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  if (hours <= 0) return `${minutes} dk`;
  if (minutes === 0) return `${hours} sa`;
  return `${hours} sa ${minutes} dk`;
};

const normalizePhoneHref = (value?: string) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits ? `tel:${digits}` : '#';
};

const buildWhatsappHref = (value?: string, message?: string) => {
  const digits = String(value || '').replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}?text=${encodeURIComponent(message || '')}` : '#';
};

const buildMapsLink = (parts: Array<string | null | undefined>) => {
  const query = parts.map((item) => String(item || '').trim()).filter(Boolean).join(', ');
  return query ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}` : '#';
};

export function PendingAssignmentDetailSheet({ shipmentId, open, onClose, title = 'Sipariş Detayı' }: PendingAssignmentDetailSheetProps) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [geocodedPickupCoords, setGeocodedPickupCoords] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    if (!open || !shipmentId) return;

    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const response: any = await shipmentServiceReal.getShipment(shipmentId);
        const mergedDetail = response?.data || response;

        if (!cancelled) {
          setDetail(mergedDetail);
        }
      } catch {
        if (!cancelled) {
          setDetail(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void load();
    return () => {
      cancelled = true;
    };
  }, [open, shipmentId]);

  useEffect(() => {
    if (!open || !detail || geocodedPickupCoords) return;
    if (detail?.pickup_latitude != null && detail?.pickup_longitude != null) return;

    const address = [detail?.pickup_name, detail?.pickup_address, detail?.pickup_district, detail?.pickup_city]
      .map((item: any) => String(item || '').trim())
      .filter(Boolean)
      .join(', ');

    if (!address) return;

    let cancelled = false;
    const geocode = async () => {
      const key = await import('../utils/googleMaps').then(m => m.getGoogleMapsApiKey());
      if (!key) return;
      try {
        const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=${key}`);
        const data = await response.json();
        const location = data?.results?.[0]?.geometry?.location;
        if (!cancelled && location) {
          setGeocodedPickupCoords({ lat: Number(location.lat), lng: Number(location.lng) });
        }
      } catch {
        if (!cancelled) {
          setGeocodedPickupCoords(null);
        }
      }
    };

    void geocode();
    return () => {
      cancelled = true;
    };
  }, [open, detail, geocodedPickupCoords]);

  if (!open) return null;

  const pickupMapsUrl = buildMapsLink([detail?.pickup_name, detail?.pickup_address, detail?.pickup_district, detail?.pickup_city]);
  const deliveryMapsUrl = buildMapsLink([detail?.delivery_name, detail?.delivery_address, detail?.delivery_district, detail?.delivery_city]);
  const pickupContactPhone = detail?.loading_contact_phone || detail?.shipment_contact_phone || detail?.source_phone || '';
  const deliveryContactPhone = detail?.delivery_contact_phone || detail?.shipment_contact_phone || detail?.source_phone || '';
  const currentLatitude = detail?.current_latitude != null ? Number(detail.current_latitude) : null;
  const currentLongitude = detail?.current_longitude != null ? Number(detail.current_longitude) : null;
  const hasCurrentLocation = currentLatitude != null && currentLongitude != null;
  const pickupWhatsappUrl = buildWhatsappHref(pickupContactPhone, `#${detail?.shipment_no || shipmentId} numaralı sevkiyat için yükleme noktası hakkında bilgi alabilir miyim?`);
  const deliveryWhatsappUrl = buildWhatsappHref(deliveryContactPhone, `#${detail?.shipment_no || shipmentId} numaralı sevkiyat için teslim noktası hakkında bilgi alabilir miyim?`);
  const pickupLatitude = detail?.pickup_latitude != null ? Number(detail.pickup_latitude) : geocodedPickupCoords?.lat ?? null;
  const pickupLongitude = detail?.pickup_longitude != null ? Number(detail.pickup_longitude) : geocodedPickupCoords?.lng ?? null;
  const distanceToPickupKm = haversineKm(
    currentLatitude,
    currentLongitude,
    pickupLatitude,
    pickupLongitude,
  );
  const estimatedArrival = estimateArrivalDuration(distanceToPickupKm);

  return (
    <div className="fixed inset-0 z-50 bg-black/40">
      <div className="absolute inset-x-0 bottom-0 max-h-[calc(100vh-1rem)] overflow-y-auto rounded-t-3xl bg-white p-5 pb-28 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between gap-3">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-amber-700">{title}</div>
            <div className="mt-1 text-lg font-bold text-slate-900">#{detail?.shipment_no || shipmentId}</div>
          </div>
          <button onClick={onClose} className="rounded-xl p-2 text-slate-500 hover:bg-slate-100">
            <X className="h-5 w-5" />
          </button>
        </div>

        {loading ? (
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">Detay yükleniyor...</div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-emerald-700"><MapPin className="h-4 w-4" /> Yükleme</div>
                <div className="mt-2 font-bold text-slate-900">{detail?.pickup_name || '-'}</div>
                <div className="mt-1 text-xs text-slate-600">{detail?.pickup_city || '-'}{detail?.pickup_district ? ` / ${detail.pickup_district}` : ''}</div>
                <div className="mt-2 text-xs text-slate-500">{detail?.pickup_address || '-'}</div>
                <div className="mt-3 text-xs font-medium text-slate-700">İrtibat: {pickupContactPhone || '-'}</div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <a href={pickupMapsUrl} target="_blank" rel="noreferrer" className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ${pickupMapsUrl !== '#' ? 'bg-emerald-600 text-white' : 'bg-slate-200 text-slate-500 pointer-events-none'}`}>
                    <MapPin className="h-4 w-4" /> Git
                  </a>
                  <a href={normalizePhoneHref(pickupContactPhone)} className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ${pickupContactPhone ? 'bg-white text-emerald-700 border border-emerald-200' : 'bg-slate-200 text-slate-500 pointer-events-none'}`}>
                    <Phone className="h-4 w-4" /> Ara
                  </a>
                  <a href={pickupWhatsappUrl} target="_blank" rel="noreferrer" aria-label="WhatsApp ile mesaj gönder" className={`inline-flex items-center justify-center rounded-xl px-3 py-2 text-xs font-semibold ${pickupContactPhone ? 'bg-green-600 text-white' : 'bg-slate-200 text-slate-500 pointer-events-none'}`}>
                    <MessageCircle className="h-4 w-4" />
                  </a>
                </div>
              </div>
              <div className="rounded-2xl border border-rose-100 bg-rose-50 p-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-rose-700"><MapPin className="h-4 w-4" /> Boşaltma</div>
                <div className="mt-2 font-bold text-slate-900">{detail?.delivery_name || '-'}</div>
                <div className="mt-1 text-xs text-slate-600">{detail?.delivery_city || '-'}{detail?.delivery_district ? ` / ${detail.delivery_district}` : ''}</div>
                <div className="mt-2 text-xs text-slate-500">{detail?.delivery_address || '-'}</div>
                <div className="mt-3 text-xs font-medium text-slate-700">İrtibat: {deliveryContactPhone || '-'}</div>
                <div className="mt-3 grid grid-cols-3 gap-2">
                  <a href={deliveryMapsUrl} target="_blank" rel="noreferrer" className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ${deliveryMapsUrl !== '#' ? 'bg-rose-600 text-white' : 'bg-slate-200 text-slate-500 pointer-events-none'}`}>
                    <MapPin className="h-4 w-4" /> Git
                  </a>
                  <a href={normalizePhoneHref(deliveryContactPhone)} className={`inline-flex items-center justify-center gap-2 rounded-xl px-3 py-2 text-xs font-semibold ${deliveryContactPhone ? 'bg-white text-rose-700 border border-rose-200' : 'bg-slate-200 text-slate-500 pointer-events-none'}`}>
                    <Phone className="h-4 w-4" /> Ara
                  </a>
                  <a href={deliveryWhatsappUrl} target="_blank" rel="noreferrer" aria-label="WhatsApp ile mesaj gönder" className={`inline-flex items-center justify-center rounded-xl px-3 py-2 text-xs font-semibold ${deliveryContactPhone ? 'bg-green-600 text-white' : 'bg-slate-200 text-slate-500 pointer-events-none'}`}>
                    <MessageCircle className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-600"><Calendar className="h-4 w-4" /> Yükleme Tarihi</div>
                <div className="mt-2 font-bold text-slate-900">{formatDateTime(detail?.pickup_date)}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-600"><Truck className="h-4 w-4" /> Araç Tipi</div>
                <div className="mt-2 font-bold text-slate-900">{detail?.assigned_vehicle_type || detail?.vehicle_spec_type || '-'}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-600"><Package className="h-4 w-4" /> Yük Tipi</div>
                <div className="mt-2 font-bold text-slate-900">{detail?.load_type || '-'}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-center gap-2 text-xs font-semibold text-slate-600"><Scale className="h-4 w-4" /> Ağırlık</div>
                <div className="mt-2 font-bold text-slate-900">{detail?.weight_kg ? `${Number(detail.weight_kg).toLocaleString('tr-TR')} Kg` : '-'}</div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold text-slate-600">Hacim</div>
                <div className="mt-2 font-bold text-slate-900">{detail?.volume_m3 ? `${Number(detail.volume_m3).toLocaleString('tr-TR')} m3` : '-'}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <div className="text-xs font-semibold text-slate-600">Mesafe</div>
                <div className="mt-2 font-bold text-slate-900">{detail?.distance_km ? `${Number(detail.distance_km).toLocaleString('tr-TR')} km` : '-'}</div>
              </div>
              <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 col-span-2">
                <div className="text-xs font-semibold text-slate-600">Mevcut Konumdan Yüklemeye Uzaklık</div>
                <div className="mt-2 font-bold text-slate-900">{distanceToPickupKm != null ? `${distanceToPickupKm} km` : '-'}</div>
                <div className="mt-1 text-xs text-slate-500">Tahmini varış: {estimatedArrival}</div>
                <div className="mt-1 text-xs text-slate-500">
                  {detail?.current_location_updated_at
                    ? `Son konum: ${formatDateTime(detail.current_location_updated_at)}`
                    : hasCurrentLocation
                      ? 'Canlı konum alındı'
                      : 'Canlı konum bulunamadı'}
                </div>
                {detail?.pickup_latitude == null && detail?.pickup_longitude == null && geocodedPickupCoords && (
                  <div className="mt-1 text-xs text-slate-500">Yükleme koordinatı adresten hesaplandı</div>
                )}
                {hasCurrentLocation && (
                  <div className="mt-1 text-xs text-slate-500">{currentLatitude?.toFixed(5)}, {currentLongitude?.toFixed(5)}</div>
                )}
                {detail?.current_location_address && (
                  <div className="mt-1 text-xs text-slate-500">{detail.current_location_address}</div>
                )}
              </div>
            </div>

            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
              <div className="flex items-center gap-2 text-xs font-semibold text-slate-600"><FileText className="h-4 w-4" /> Sipariş Notu</div>
              <div className="mt-2 text-slate-900">{detail?.notes || '-'}</div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
